package org.gymly.i18n

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable

/** Textos de las pantallas de clientes, ficha del cliente y monedero digital. */
interface ClientsStrings {
    // ---------- Listado de clientes ----------
    val title: String
    val subtitle: String
    val filterLast7Days: String
    val filterLast30Days: String
    val filterAllDates: String
    val kpiTotalClients: String
    val kpiActive: String
    val kpiInactive: String
    val kpiWithApp: String
    val directoryTitle: String
    val newClient: String
    val searchPlaceholder: String
    val emptyList: String
    val colClient: String
    val colApp: String
    val viewAction: String
    val clientsLoadFailed: String
    val clientSaveFailed: String
    val clientDeleteFailed: String
    val addClientTitle: String
    val editClientTitle: String
    val deleteClientTitle: String
    fun deleteClientConfirm(name: String): String

    // ---------- Ficha del cliente ----------
    val detailTitle: String
    val clientPhoto: String
    val branch: String
    val documentLabel: String
    val terminalHikvision: String
    val syncOk: String
    val syncWithWarning: String
    val syncPending: String
    val syncWarningTitle: String
    val terminalSyncFailed: String

    // ---------- Formulario de cliente ----------
    val firstNameRequired: String
    val lastNameRequired: String
    val sexRequired: String
    val addressRequired: String
    val emailOptional: String
    val phoneOptional: String
    val emailPlaceholder: String
    val phonePlaceholder: String
    val documentPlaceholder: String
    val addressPlaceholder: String
    val appPortalCheckbox: String
    val newAppPasswordOptional: String
    val memberAppPasswordRequired: String
    val passwordPlaceholder: String
    val firstNameRequiredError: String
    val lastNameRequiredError: String
    val emailInvalidError: String
    val sexRequiredError: String
    val addressRequiredError: String
    val portalPasswordTooShortError: String

    // ---------- Monedero digital ----------
    val wallet: String
    val walletSubtitle: String
    /** Sin punto final: se completa con `walletMigrationHint`. */
    val walletLoadFailed: String
    fun walletMigrationHint(base: String): String
    val searchMemberPlaceholder: String
    fun membersWithCredit(count: Int): String
    fun membersWithCreditFiltered(shown: Int, total: Int): String
    val walletEmpty: String
    val noMemberMatchesFilter: String
    val noMembersOnPage: String
    val walletDetailLoadFailed: String
    val detailLoadFailed: String
    val balanceDetailTitle: String
    val applyCreditToPendingOrder: String
    val applyAction: String
    fun creditAppliedToOrder(order: String): String
    fun creditAppliedAmount(amount: String): String
    /** Sin punto final: lo consume `userMessage(fallback)`. */
    val creditApplyFailed: String
    val noWalletMovements: String
    val colConcept: String
    val colBalance: String
    val creditBalance: String
    val viewDetail: String
    val memberColumn: String
    val viewWalletDetail: String
    val walletMovements: String
    val noWalletActivity: String
    fun balanceValue(amount: String): String

    // ---------- Banner de saldo a favor ----------
    /** Con punto final: se muestra tal cual en el banner. */
    val bannerWalletLoadFailed: String
    /** Con punto final: se muestra tal cual en el banner. */
    val bannerCreditApplyFailed: String
    val memberCreditHint: String
    val loadingBalance: String
    fun applicableToCharge(amount: String): String
    fun reservedForCharge(amount: String): String
    fun amountToUse(symbol: String): String
    val useAll: String
    fun maxApplicableHint(amount: String): String
    val leaveEmptyHint: String
    fun willUseHint(amount: String): String
    fun useAmount(amount: String): String
    val applyOnSaveHint: String
    val reducesPendingHint: String
    fun walletBalanceLine(amount: String): String
    val walletBalanceFooterHint: String

    // ---------- Gráficas del monedero ----------
    val overviewTitle: String
    val totalInWallets: String
    val membersWithBalance: String
    val creditBalancesTitle: String
    val totalAccumulated: String
    val balanceTrend: String
    val singleCreditRecorded: String
    fun minValue(amount: String): String
    fun maxValue(amount: String): String

    // ---------- Foto de acceso ----------
    val accessPhotoTitle: String
    val accessPhotoHint: String
    val photoAlt: String

    // ---------- Captura en terminal ----------
    val captureTitle: String
    val captureHint: String
    val terminalLabel: String
    val selectTerminal: String
    val noActiveTerminals: String
    val terminalsLoadFailed: String
    val captureFailed: String
    val capturing: String
    val captureNow: String
}

internal object ClientsEs : ClientsStrings {
    // ---------- Listado de clientes ----------
    override val title = "Clientes"
    override val subtitle =
        "Perfil y ficha del cliente desde /api/clients (nombre, contacto, estado, notas)."
    override val filterLast7Days = "7 días"
    override val filterLast30Days = "30 días"
    override val filterAllDates = "Todos"
    override val kpiTotalClients = "Total de clientes"
    override val kpiActive = "Activos"
    override val kpiInactive = "Inactivos"
    override val kpiWithApp = "Usan la app"
    override val directoryTitle = "Directorio de clientes"
    override val newClient = "+ Nuevo cliente"
    override val searchPlaceholder = "Buscar..."
    override val emptyList = "No hay clientes todavía."
    override val colClient = "Cliente"
    override val colApp = "App"
    override val viewAction = "Ver"
    override val clientsLoadFailed = "No se pudieron cargar los clientes."
    override val clientSaveFailed = "No se pudo guardar el cliente."
    override val clientDeleteFailed = "No se pudo eliminar el cliente."
    override val addClientTitle = "Agregar cliente"
    override val editClientTitle = "Editar cliente"
    override val deleteClientTitle = "Eliminar cliente"
    override fun deleteClientConfirm(name: String) =
        "¿Seguro que deseas eliminar a $name? Esta acción no se puede deshacer."

    // ---------- Ficha del cliente ----------
    override val detailTitle = "Detalle del cliente"
    override val clientPhoto = "Foto del cliente"
    override val branch = "Sucursal"
    override val documentLabel = "Documento"
    override val terminalHikvision = "Terminal Hikvision"
    override val syncOk = "Sincronizado"
    override val syncWithWarning = "Sincronizado con aviso"
    override val syncPending = "Pendiente de enviar al equipo"
    override val syncWarningTitle = "Aviso de sincronización"
    override val terminalSyncFailed = "No se pudo sincronizar con el terminal de acceso."

    // ---------- Formulario de cliente ----------
    override val firstNameRequired = "Nombre *"
    override val lastNameRequired = "Apellido *"
    override val sexRequired = "Sexo *"
    override val addressRequired = "Dirección *"
    override val emailOptional = "Correo (opcional)"
    override val phoneOptional = "Teléfono (opcional)"
    override val emailPlaceholder = "correo@ejemplo.com"
    override val phonePlaceholder = "Ej. +58 412 0000000"
    override val documentPlaceholder = "Cédula o pasaporte"
    override val addressPlaceholder = "Calle, número, ciudad"
    override val appPortalCheckbox = "App (portal de miembro habilitado)"
    override val newAppPasswordOptional = "Nueva contraseña app (opcional)"
    override val memberAppPasswordRequired = "Contraseña app socio *"
    override val passwordPlaceholder = "Mínimo 8 caracteres"
    override val firstNameRequiredError = "El nombre es obligatorio."
    override val lastNameRequiredError = "El apellido es obligatorio."
    override val emailInvalidError =
        "El correo debe incluir @ y un dominio (ej. nombre@correo.com)."
    override val sexRequiredError = "El sexo es obligatorio."
    override val addressRequiredError = "La dirección es obligatoria."
    override val portalPasswordTooShortError =
        "La contraseña del portal debe tener al menos 8 caracteres."

    // ---------- Monedero digital ----------
    override val wallet = "Monedero digital"
    override val walletSubtitle =
        "Crédito disponible por pagos en exceso. Úsalo desde órdenes o ventas con «Usar saldo a favor»."
    override val walletLoadFailed = "No se pudo cargar el monedero digital"
    override fun walletMigrationHint(base: String) =
        "$base. Si acabas de actualizar, reinicia el backend para aplicar la migración V61 (client_accounts)."

    override val searchMemberPlaceholder = "Buscar socio…"
    override fun membersWithCredit(count: Int) = "$count socios con saldo a favor"
    override fun membersWithCreditFiltered(shown: Int, total: Int) =
        "$shown de $total socios con saldo"

    override val walletEmpty = "Ningún socio tiene saldo a favor en el monedero."
    override val noMemberMatchesFilter = "Ningún socio coincide con el filtro."
    override val noMembersOnPage = "Sin socios en esta página."
    override val walletDetailLoadFailed = "No se pudo cargar el detalle del monedero"
    override val detailLoadFailed = "No se pudo cargar el detalle"
    override val balanceDetailTitle = "Detalle del saldo"
    override val applyCreditToPendingOrder = "Aplicar saldo a orden pendiente"
    override val applyAction = "Aplicar"
    override fun creditAppliedToOrder(order: String) = "Saldo aplicado a $order."
    override fun creditAppliedAmount(amount: String) = "Se aplicaron $amount de saldo."
    override val creditApplyFailed = "No se pudo aplicar el saldo"
    override val noWalletMovements = "Sin movimientos en el monedero."
    override val colConcept = "Concepto"
    override val colBalance = "Saldo"
    override val creditBalance = "Saldo a favor"
    override val viewDetail = "Ver detalle →"
    override val memberColumn = "Socio"
    override val viewWalletDetail = "Ver detalle del monedero"
    override val walletMovements = "Movimientos del monedero"
    override val noWalletActivity = "Sin abonos ni usos de saldo registrados."
    override fun balanceValue(amount: String) = "Saldo $amount"

    // ---------- Banner de saldo a favor ----------
    override val bannerWalletLoadFailed = "No se pudo cargar el monedero digital."
    override val bannerCreditApplyFailed = "No se pudo aplicar el saldo."
    override val memberCreditHint = "Saldo a favor del socio — puedes usarlo en este cobro."
    override val loadingBalance = "Cargando saldo…"
    override fun applicableToCharge(amount: String) = "Aplicable a este cobro: $amount"
    override fun reservedForCharge(amount: String) = "Reservado para este cobro: $amount"
    override fun amountToUse(symbol: String) = "Monto a usar ($symbol)"
    override val useAll = "Todo"
    override fun maxApplicableHint(amount: String) =
        "El máximo aplicable es $amount; se usará ese monto."

    override val leaveEmptyHint = "Déjalo vacío para usar todo lo aplicable, o escribe una parte."
    override fun willUseHint(amount: String) =
        "Se usará $amount y el resto se cobra con otros métodos."

    override fun useAmount(amount: String) = "Usar $amount"
    override val applyOnSaveHint = "Se aplicará al guardar la orden."
    override val reducesPendingHint = "Reduce el monto pendiente de cobro."
    override fun walletBalanceLine(amount: String) = "Monedero digital: $amount"
    override val walletBalanceFooterHint =
        "Podrás usar este saldo al configurar el cobro del socio."

    // ---------- Gráficas del monedero ----------
    override val overviewTitle = "Resumen general"
    override val totalInWallets = "Total en monederos"
    override val membersWithBalance = "Socios con saldo"
    override val creditBalancesTitle = "Saldos a favor"
    override val totalAccumulated = "Total acumulado"
    override val balanceTrend = "Evolución del saldo"
    override val singleCreditRecorded = "Un solo abono registrado."
    override fun minValue(amount: String) = "Mín $amount"
    override fun maxValue(amount: String) = "Máx $amount"

    // ---------- Foto de acceso ----------
    override val accessPhotoTitle = "Foto para acceso (rostro)"
    override val accessPhotoHint =
        "Usa los iconos para subir un archivo o capturar en el terminal Hikvision."
    override val photoAlt = "Foto"

    // ---------- Captura en terminal ----------
    override val captureTitle = "Capturar en terminal Hikvision"
    override val captureHint =
        "Pida a la persona que mire a la cámara del equipo. La captura puede tardar unos segundos."
    override val terminalLabel = "Terminal"
    override val selectTerminal = "Seleccionar terminal"
    override val noActiveTerminals = "No hay terminales activos. Configura uno en Configuración → Acceso."
    override val terminalsLoadFailed = "No se pudieron cargar los terminales."
    override val captureFailed = "No se pudo capturar la foto en el terminal."
    override val capturing = "Capturando…"
    override val captureNow = "Capturar ahora"
}

internal object ClientsEn : ClientsStrings {
    // ---------- Listado de clientes ----------
    override val title = "Clients"
    override val subtitle =
        "Client profile and record from /api/clients (name, contact, status, notes)."
    override val filterLast7Days = "Last 7 days"
    override val filterLast30Days = "Last 30 days"
    override val filterAllDates = "All time"
    override val kpiTotalClients = "Total clients"
    override val kpiActive = "Active"
    override val kpiInactive = "Inactive"
    override val kpiWithApp = "Using the app"
    override val directoryTitle = "Client directory"
    override val newClient = "+ New client"
    override val searchPlaceholder = "Search..."
    override val emptyList = "No clients yet."
    override val colClient = "Client"
    override val colApp = "App"
    override val viewAction = "View"
    override val clientsLoadFailed = "Could not load the clients."
    override val clientSaveFailed = "Could not save the client."
    override val clientDeleteFailed = "Could not delete the client."
    override val addClientTitle = "Add client"
    override val editClientTitle = "Edit client"
    override val deleteClientTitle = "Delete client"
    override fun deleteClientConfirm(name: String) =
        "Delete $name? This action cannot be undone."

    // ---------- Ficha del cliente ----------
    override val detailTitle = "Client details"
    override val clientPhoto = "Client photo"
    override val branch = "Branch"
    override val documentLabel = "ID number"
    override val terminalHikvision = "Hikvision terminal"
    override val syncOk = "Synced"
    override val syncWithWarning = "Synced with a warning"
    override val syncPending = "Waiting to be sent to the device"
    override val syncWarningTitle = "Sync warning"
    override val terminalSyncFailed = "Could not sync with the access terminal."

    // ---------- Formulario de cliente ----------
    override val firstNameRequired = "First name *"
    override val lastNameRequired = "Last name *"
    override val sexRequired = "Sex *"
    override val addressRequired = "Address *"
    override val emailOptional = "Email (optional)"
    override val phoneOptional = "Phone (optional)"
    override val emailPlaceholder = "name@example.com"
    override val phonePlaceholder = "e.g. +58 412 0000000"
    override val documentPlaceholder = "ID or passport"
    override val addressPlaceholder = "Street, number, city"
    override val appPortalCheckbox = "App (member portal enabled)"
    override val newAppPasswordOptional = "New app password (optional)"
    override val memberAppPasswordRequired = "Member app password *"
    override val passwordPlaceholder = "At least 8 characters"
    override val firstNameRequiredError = "First name is required."
    override val lastNameRequiredError = "Last name is required."
    override val emailInvalidError =
        "The email must include @ and a domain (e.g. name@example.com)."
    override val sexRequiredError = "Sex is required."
    override val addressRequiredError = "Address is required."
    override val portalPasswordTooShortError =
        "The portal password must be at least 8 characters."

    // ---------- Monedero digital ----------
    override val wallet = "Digital wallet"
    override val walletSubtitle =
        "Credit available from overpayments. Use it from orders or sales with “Use credit balance”."
    override val walletLoadFailed = "Could not load the digital wallet"
    override fun walletMigrationHint(base: String) =
        "$base. If you just updated, restart the backend to apply migration V61 (client_accounts)."

    override val searchMemberPlaceholder = "Search member…"
    override fun membersWithCredit(count: Int) = "$count members with a credit balance"
    override fun membersWithCreditFiltered(shown: Int, total: Int) =
        "$shown of $total members with credit"

    override val walletEmpty = "No member has a credit balance in the wallet."
    override val noMemberMatchesFilter = "No member matches the filter."
    override val noMembersOnPage = "No members on this page."
    override val walletDetailLoadFailed = "Could not load the wallet details"
    override val detailLoadFailed = "Could not load the details"
    override val balanceDetailTitle = "Balance details"
    override val applyCreditToPendingOrder = "Apply credit to a pending order"
    override val applyAction = "Apply"
    override fun creditAppliedToOrder(order: String) = "Credit applied to $order."
    override fun creditAppliedAmount(amount: String) = "Applied $amount of credit."
    override val creditApplyFailed = "Could not apply the credit"
    override val noWalletMovements = "No wallet activity yet."
    override val colConcept = "Type"
    override val colBalance = "Balance"
    override val creditBalance = "Credit balance"
    override val viewDetail = "View details →"
    override val memberColumn = "Member"
    override val viewWalletDetail = "View wallet details"
    override val walletMovements = "Wallet activity"
    override val noWalletActivity = "No credits or credit uses recorded."
    override fun balanceValue(amount: String) = "Balance $amount"

    // ---------- Banner de saldo a favor ----------
    override val bannerWalletLoadFailed = "Could not load the digital wallet."
    override val bannerCreditApplyFailed = "Could not apply the credit."
    override val memberCreditHint = "The member has credit — you can use it for this charge."
    override val loadingBalance = "Loading balance…"
    override fun applicableToCharge(amount: String) = "Available for this charge: $amount"
    override fun reservedForCharge(amount: String) = "Reserved for this charge: $amount"
    override fun amountToUse(symbol: String) = "Amount to use ($symbol)"
    override val useAll = "All"
    override fun maxApplicableHint(amount: String) =
        "The most you can apply is $amount; that amount will be used."

    override val leaveEmptyHint = "Leave it empty to use everything available, or enter part of it."
    override fun willUseHint(amount: String) =
        "$amount will be used and the rest charged with other methods."

    override fun useAmount(amount: String) = "Use $amount"
    override val applyOnSaveHint = "It will be applied when you save the order."
    override val reducesPendingHint = "Reduces the amount left to charge."
    override fun walletBalanceLine(amount: String) = "Digital wallet: $amount"
    override val walletBalanceFooterHint =
        "You can use this balance when you set up the member's charge."

    // ---------- Gráficas del monedero ----------
    override val overviewTitle = "Overview"
    override val totalInWallets = "Total in wallets"
    override val membersWithBalance = "Members with credit"
    override val creditBalancesTitle = "Credit balances"
    override val totalAccumulated = "Accumulated total"
    override val balanceTrend = "Balance over time"
    override val singleCreditRecorded = "Only one credit recorded."
    override fun minValue(amount: String) = "Min $amount"
    override fun maxValue(amount: String) = "Max $amount"

    // ---------- Foto de acceso ----------
    override val accessPhotoTitle = "Access photo (face)"
    override val accessPhotoHint =
        "Use the icons to upload a file or capture it on the Hikvision terminal."
    override val photoAlt = "Photo"

    // ---------- Captura en terminal ----------
    override val captureTitle = "Capture on Hikvision terminal"
    override val captureHint =
        "Ask the person to look at the device camera. The capture may take a few seconds."
    override val terminalLabel = "Terminal"
    override val selectTerminal = "Select terminal"
    override val noActiveTerminals = "No active terminals. Set one up in Settings → Access."
    override val terminalsLoadFailed = "Could not load the terminals."
    override val captureFailed = "Could not capture the photo on the terminal."
    override val capturing = "Capturing…"
    override val captureNow = "Capture now"
}

@Composable
@ReadOnlyComposable
fun clientsStrings(): ClientsStrings = pickStrings(ClientsEs, ClientsEn)
